import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronRightIcon } from "../../icons";
import { formatFiatLabel, formatSwapAmount } from "../../utils/format";
import QuoteExpiryTimer from "./QuoteExpiryTimer";
import { useCryptoRFQ } from "../../hooks/useCryptoRFQ";
import { CreateCryptoOrder } from "../../../redux/actions/CryptoTransaction/Liquidity";
import Skeleton from "../../../components/Common/Skeleton";
import { cryptoPolicyURL, cryptoTremsCondiations } from "../../../constants";

const SummaryRow = ({ label, value, valueClassName = "text-base font-medium text-text-primary" }) => (
  <div className="w-full px-4">
    <div className="flex w-full items-center justify-between leading-[21px]">
      <p className="text-sm font-normal text-text-secondary">{label}</p>
      <p className={`w-[188px] text-right ${valueClassName}`}>{value}</p>
    </div>
  </div>
);

const SwapSummaryCard = ({ asset, amount, fiatValue }) => (
  <div className="flex w-[140px] shrink-0 flex-col items-center gap-4 rounded-[8px] py-4 sm:w-[222px]">
    <div className="flex size-[60px] items-center justify-center overflow-hidden rounded-full">
      <img
        src={asset?.logo}
        alt={asset?.currency_code}
        className="size-[60px] rounded-full object-cover"
      />
    </div>
    <div className="flex flex-col items-center gap-[6px] text-center">
      <p className="whitespace-nowrap text-base font-semibold leading-6 text-text-primary sm:text-2xl">
        {formatSwapAmount(amount, asset?.currency_code)}
      </p>
      <p className="w-full text-xs font-semibold leading-[21px] text-[#4ab932]">
        ≈ {formatFiatLabel(fiatValue)}
      </p>
    </div>
  </div>
);

const ConfirmSwapStep = ({
  fromAsset,
  toAsset,
  fromAmount,
  setIsProcessing,
  setStep,
  setError,
}) => {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.ownCompany.company);
  const short_name = process.env.REACT_APP_COMPANY_SHORT_NAME;
  
  const handleError = (errorMessage) => {
    setError(errorMessage);
    if (errorMessage) {
      setStep(1); // Go back to the previous step on error
    }
  }

  // Consume custom RFQ hook for swap breakdown & quote details
  const { rfq, quoteId, timeLeft, totalDuration, isExpired, loading: rfqLoading, refetch: refetchRFQ } = useCryptoRFQ({
    cryptoCurrency: fromAsset?.currency_code,
    fiatCurrency: toAsset?.currency_code,
    side: "swap",
    amount: fromAmount,
    inputMode: "crypto",
    cryptoAccountId: fromAsset?.id,
    fiatAccountId: toAsset?.id,
    setError: handleError,
    error: null,
  });

  const { fromValue, toAmount, toFiat, fromFiat, totalSpend, debitDate } = useMemo(() => {
    const today = new Date();
    const dateLabel = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;

    const rfqData = rfq?.data;

    // 1. Calculate FROM Value (from response or input amount)
    const calculatedFrom = Number(rfqData?.from_amount ?? fromAmount ?? 0);

    // 2. Calculate TO Amount (from response to_amount or ratio calculation fallback)
    const fallbackToAmount = (calculatedFrom * (fromAsset?.unit_price_usd ?? 1)) / (toAsset?.unit_price_usd ?? 1);
    const calculatedTo = Number(rfqData?.to_amount ?? rfqData?.quantity ?? fallbackToAmount);

    // 3. Calculate Fiat Equivalents
    const fromFiatValue = calculatedFrom * (fromAsset?.unit_price_usd ?? 1);
    const toFiatValue = calculatedTo * (toAsset?.unit_price_usd ?? 1);

    // 4. Calculate Total Spend & Fee
    const fee = Number(rfq?.fee?.total_fee) || 0;
    const totalSpendVal = fromFiatValue + fee;
    // const currencySymbol = rfq?.fee?.currency_short || fromAsset?.currency_code || "USD";
    const currencySymbol = fromAsset?.currency_code;

    return {
      fromValue: calculatedFrom,
      toAmount: calculatedTo,
      toFiat: toFiatValue,
      fromFiat: fromFiatValue,
      totalSpend: `${formatSwapAmount(totalSpendVal)} ${currencySymbol}`,
      debitDate: dateLabel,
    };
  }, [fromAmount, fromAsset, toAsset, rfq]);

  // Refresh RFQ quote on timer expiration/reset
  const handleRefresh = () => {
    refetchRFQ();
  };

  const onConfirmOrder = async () => {
    if (!quoteId) return;

    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("rfq_id", quoteId);
      formData.append("instrument", `${fromAsset?.currency_code}-${toAsset?.currency_code}`);
      formData.append("side", "swap");
      formData.append("from_amount", fromAmount);
      formData.append("quantity", fromAmount);
      formData.append("from_account_id", fromAsset.id);
      formData.append("to_account_id", toAsset.id);

      const order = await dispatch(CreateCryptoOrder(formData));

      if (order?.success) {
        setStep(3);
      } else {
        setError(order?.message ?? "Something went wrong, please try again later.");
      }
    } catch (err) {
      console.error("onConfirmOrder ERROR: ", err);
      setError("Failed to execute order. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const isLoading = rfqLoading || !rfq;

  return (
    <div className="flex h-full min-h-0 flex-col justify-between overflow-y-auto hide-scrollbar px-6 pb-6 pt-10 font-outfit">
      <div className="flex flex-col gap-6">
        {/* Asset Swap Cards */}
        <div className="relative flex w-full items-center justify-between px-2">
          <SwapSummaryCard
            asset={fromAsset}
            amount={fromValue}
            fiatValue={fromFiat}
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-text-secondary">
            <ChevronRightIcon className="size-[34px]" />
          </div>
          <SwapSummaryCard
            asset={toAsset}
            amount={toAmount}
            fiatValue={toFiat}
          />
        </div>

        {/* Payment Breakdown */}
        <div className="flex w-full flex-col gap-[12px]">
          <div className="w-full px-4">
            <div className="flex w-full items-center justify-between">
              <p className="text-sm font-normal leading-[21px] text-text-secondary">
                Payment
              </p>
              <div className="flex items-center gap-2">
                {fromAsset?.logo && (
                  <div className="flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#fff7eb]">
                    <img
                      src={fromAsset.logo}
                      alt={fromAsset?.currency_code}
                      className="size-5 rounded-full object-cover"
                    />
                  </div>
                )}
                <p className="whitespace-pre text-sm font-medium leading-[19.5px] text-text-primary">
                  {fromAsset?.title ?? `${fromAsset?.currency_code}`}
                </p>
              </div>
            </div>
          </div>

          {/* Loading Skeleton vs Live RFQ Breakdown */}
          {isLoading ? (
            <div className="flex w-full flex-col gap-3">
              <div className="flex justify-between px-4">
                <Skeleton className="h-4 w-28 animate-pulse rounded" />
                <Skeleton className="h-4 w-20 animate-pulse rounded" />
              </div>
              <div className="flex justify-between px-4">
                <Skeleton className="h-4 w-32 animate-pulse rounded" />
                <Skeleton className="h-4 w-20 animate-pulse rounded" />
              </div>
              <div className="flex justify-between px-4">
                <Skeleton className="h-4 w-32 animate-pulse rounded" />
                <Skeleton className="h-4 w-20 animate-pulse rounded" />
              </div>
              <div className="w-full border-t border-divider" />
              <div className="flex justify-between px-4">
                <Skeleton className="h-5 w-24 animate-pulse rounded" />
                <Skeleton className="h-5 w-24 animate-pulse rounded" />
              </div>
            </div>
          ) : (
            <div className="flex w-full flex-col gap-[12px]">
              {/* Detailed Fee Charges */}
              {rfq?.fee?.charges?.map((charge, idx) => (
                <SummaryRow
                  key={idx}
                  label={`${charge.label} (${charge.rate}%)`}
                  // value={`${formatSwapAmount(charge.amount)} ${rfq.fee.currency_short}`}
                  value={`${formatSwapAmount(charge.amount)} ${fromAsset?.currency_code}`}
                />
              ))}

              {/* Separator Divider */}
              <div className="w-full border-t border-divider" />

              {/* Total Spend */}
              <SummaryRow
                label="Total Spend"
                value={totalSpend}
                valueClassName="text-base font-semibold text-text-primary"
              />
            </div>
          )}
        </div>

        {/* Quote Expiry Timer Component */}
        <QuoteExpiryTimer
          timeLeft={timeLeft}
          totalDuration={totalDuration}
          isExpired={isExpired}
          loading={rfqLoading || !rfq}
          onRefresh={handleRefresh}
        />
      </div>

      {/* Footer / Confirm CTA */}
      <div className="flex shrink-0 flex-col gap-4 pt-4">
        <div className="flex items-start gap-1">
          <p className="text-center text-[11px] leading-relaxed text-text-secondary sm:text-xs">
            By clicking <span className="font-semibold text-text-primary">Confirm</span>, you agree to the{" "}
            <a
              href={cryptoPolicyURL}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[#4ab932] transition-colors hover:underline"
            >
              Buy &amp; Sell Crypto Services Terms
            </a>
            , {company?.short_name || short_name}&apos;s{" "}
            <a
              href={cryptoTremsCondiations}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[#4ab932] transition-colors hover:underline"
            >
              Terms of Use
            </a>{" "}
            and{" "}
            <a
              href={cryptoPolicyURL}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[#4ab932] transition-colors hover:underline"
            >
              Privacy Policy
            </a>
            , and authorize {company?.short_name || short_name} to debit your chosen payment method for the amount above on {debitDate}.
          </p>
        </div>

        <button
          type="button"
          disabled={isExpired || isLoading || !quoteId}
          onClick={onConfirmOrder}
          className="custom-transition flex h-12 w-full items-center justify-center rounded-[10px] bg-[#4ab933] text-sm font-semibold leading-[21px] text-white enabled:hover:bg-[#3fa82c] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Confirm Swap
        </button>
      </div>
    </div>
  );
};

export default ConfirmSwapStep;